import React, { useState, useEffect } from 'react';

export function RegistrationForm({ event, onClose, onSuccess, emailFromInvite = null, isGuest = false }) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: emailFromInvite || '',
    whatsapp_number: '',
    attendance_status: 'yes',
    comments: '',
  });
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [accountCreated, setAccountCreated] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const generateTicketNumber = () => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8).toUpperCase();
    return `TKT-${event.id}-${random}`;
  };

  const generateQRCode = (ticketNumber) => {
    // Using QR code API - returns a simple encoded string
    // In production, you'd use a QR library like qrcode.react
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(ticketNumber)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    // Validation
    if (!formData.first_name.trim()) {
      setError('First name is required');
      setIsLoading(false);
      return;
    }

    if (!formData.last_name.trim()) {
      setError('Last name is required');
      setIsLoading(false);
      return;
    }

    if (!formData.whatsapp_number.trim()) {
      setError('WhatsApp number is required');
      setIsLoading(false);
      return;
    }

    // For guest/invited users, email is required
    if (isGuest && !formData.email.trim()) {
      setError('Email is required to register');
      setIsLoading(false);
      return;
    }

    try {
      const ticketNumber = generateTicketNumber();
      const qrCode = generateQRCode(ticketNumber);
      
      // Get user_id from session if logged in
      const sessionToken = localStorage.getItem('session_token');
      const userEmail = localStorage.getItem('user_email');
      const userId = localStorage.getItem('user_id');
      
      let finalEmail = formData.email.trim() || userEmail || null;
      let newSessionToken = sessionToken;
      let newUserId = userId || null;
      let accountJustCreated = false;

      // If guest is registering with an email (not logged in)
      if (isGuest && !sessionToken && formData.email.trim()) {
        try {
          const emailToCheck = formData.email.trim();
          console.log('🔍 Checking if email exists:', emailToCheck);
          
          // First, check if email already exists
          const checkEmailResponse = await fetch(`/api/users/by-email/${encodeURIComponent(emailToCheck)}`);
          
          if (checkEmailResponse.ok) {
            // Email exists - just use that user's ID
            const userData = await checkEmailResponse.json();
            if (userData.user && userData.user.id) {
              newUserId = userData.user.id;
              console.log('✅ Email exists, using user_id:', newUserId);
            }
          } else if (checkEmailResponse.status === 404) {
            // Email doesn't exist - create account first
            console.log('📝 Email not found, creating account...');
            const tempPassword = Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 10);
            
            const registerResponse = await fetch('/api/register', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                firstName: formData.first_name.trim(),
                lastName: formData.last_name.trim(),
                email: emailToCheck,
                password: tempPassword,
              }),
            });

            const registerData = await registerResponse.json();

            if (!registerResponse.ok) {
              throw new Error(registerData.message || 'Failed to create account');
            }

            // Account created successfully - fetch the user to get user_id
            accountJustCreated = true;
            setAccountCreated(true);
            
            try {
              const userFetchResponse = await fetch(`/api/users/by-email/${encodeURIComponent(emailToCheck)}`);
              if (userFetchResponse.ok) {
                const userData = await userFetchResponse.json();
                if (userData.user && userData.user.id) {
                  newUserId = userData.user.id;
                  console.log('✅ Account created, user_id:', newUserId);
                }
              }
            } catch (fetchErr) {
              console.error('Error fetching user after creation:', fetchErr);
            }
          }
        } catch (err) {
          console.error('Error checking/creating account:', err);
          setError(err.message || 'Failed to process registration. Please try again.');
          setIsLoading(false);
          return;
        }
      }

      // If the event requires payment, initiate Stripe or PayPal checkout
      if (event.price && event.price > 0) {
        const checkoutData = {
          event_id: event.id,
          event_name: event.name,
          price: event.price,
          user_id: newUserId || null,
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          email: finalEmail || null,
          whatsapp_number: formData.whatsapp_number.trim(),
          ticket_number: ticketNumber,
          qr_code: qrCode
        };

        const endpoint = paymentMethod === 'paypal' ? '/api/create-paypal-order' : '/api/create-checkout-session';
        const checkoutResponse = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(checkoutData)
        });

        const checkoutResult = await checkoutResponse.json();

        if (checkoutResponse.ok && checkoutResult.url) {
          window.location.href = checkoutResult.url;
          return;
        } else {
          throw new Error(checkoutResult.error || checkoutResult.message || 'Failed to initialize payment');
        }
      }

      // If no payment required, save the event registration as usual
      const registrationData = {
        event_id: event.id,
        user_id: newUserId,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: finalEmail,
        whatsapp_number: formData.whatsapp_number.trim(),
        attendance_status: formData.attendance_status,
        comments: formData.comments.trim() || null,
        ticket_number: ticketNumber,
        qr_code: qrCode,
        is_paid: 0,
        session_token: newSessionToken || null,
      };

      console.log('📤 Sending registration data:', registrationData);

      const response = await fetch('/api/registrations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(newSessionToken && { 'X-Session-Token': newSessionToken }),
        },
        body: JSON.stringify(registrationData),
      });

      const data = await response.json();
      console.log('📥 Registration response:', { status: response.status, ok: response.ok, data });

      if (!response.ok) {
        throw new Error(data.message || 'Failed to register for event');
      }

      // Show appropriate success message
      if (accountJustCreated) {
        setSuccess(`✅ Account created and registered! You'll receive a password reset email at ${finalEmail}. Login to view your ticket. Your ticket number is: ${ticketNumber}`);
      } else {
        setSuccess(`Successfully registered! Go to your dashboard to view your ticket. Your ticket number is: ${ticketNumber}`);
      }
      
      // Reset form
      setFormData({
        first_name: '',
        last_name: '',
        email: emailFromInvite || '',
        whatsapp_number: '',
        attendance_status: 'yes',
        comments: '',
      });

      // Notify parent or redirect
      if (formData._redirectTarget === 'donate') {
        setTimeout(() => {
          window.location.href = '/donate';
        }, 1500);
      } else if (onSuccess) {
        setTimeout(() => onSuccess(data), 2000);
      }
    } catch (err) {
      setError(err.message || 'An error occurred during registration');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <h5 style={{ marginBottom: '20px', color: '#333', fontWeight: 'bold' }}>
        Register for {event.name} 
        {event.price > 0 && <span className="ms-2 badge bg-success">${event.price}</span>}
      </h5>

      {error && (
        <div style={{ padding: '12px', backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '6px', color: '#721c24', marginBottom: '15px' }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{ padding: '12px', backgroundColor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '6px', color: '#155724', marginBottom: '15px' }}>
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500', color: '#333' }}>
              First Name *
            </label>
            <input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              placeholder="John"
              autoComplete="given-name"
              style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}
              required
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500', color: '#333' }}>
              Last Name *
            </label>
            <input
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              placeholder="Doe"
              autoComplete="family-name"
              style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}
              required
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500', color: '#333' }}>
              WhatsApp Number * (with country code)
            </label>
            <input
              type="tel"
              name="whatsapp_number"
              value={formData.whatsapp_number}
              onChange={handleChange}
              placeholder="+1234567890"
              autoComplete="tel-national"
              style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}
              required
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500', color: '#333' }}>
              Email (Optional)
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="john@example.com"
              autoComplete="email"
              style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}
            />
          </div>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500', color: '#333' }}>
            Will you attend? *
          </label>
          <select
            name="attendance_status"
            value={formData.attendance_status}
            onChange={handleChange}
            style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}
          >
            <option value="yes">Yes, I will attend</option>
            <option value="maybe">Maybe</option>
            <option value="no">No, I cannot attend</option>
          </select>
        </div>

        {event.price > 0 && (
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500', color: '#333' }}>
              Payment Method
            </label>
            <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#333' }}>
                <input
                  type="radio"
                  name="payment_method"
                  value="stripe"
                  checked={paymentMethod === 'stripe'}
                  onChange={() => setPaymentMethod('stripe')}
                />
                Pay with Stripe
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#333' }}>
                <input
                  type="radio"
                  name="payment_method"
                  value="paypal"
                  checked={paymentMethod === 'paypal'}
                  onChange={() => setPaymentMethod('paypal')}
                />
                Pay with PayPal
              </label>
            </div>
            <small style={{ display: 'block', marginTop: '8px', color: '#6c757d' }}>
              Select Stripe or PayPal for your ticket purchase.
            </small>
          </div>
        )}

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500', color: '#333' }}>
            Comments/Suggestions (Optional)
          </label>
          <textarea
            name="comments"
            value={formData.comments}
            onChange={handleChange}
            placeholder="Any suggestions or comments for the event organizer..."
            rows={4}
            style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', fontFamily: 'inherit' }}
          />
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={onClose}
            style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}
            disabled={isLoading}
          >
            Cancel
          </button>
          
          {event.price > 0 ? (
            <button
              type="submit"
              style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', opacity: isLoading ? 0.7 : 1 }}
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : `Pay $${event.price} with ${paymentMethod === 'paypal' ? 'PayPal' : 'Stripe'} & Book`}
            </button>
          ) : (
            <>
              <button
                type="submit"
                onClick={() => {
                  // Standard free registration
                  setFormData(prev => ({ ...prev, _redirectTarget: 'success' }));
                }}
                style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', opacity: isLoading ? 0.7 : 1 }}
                disabled={isLoading}
              >
                {isLoading ? 'Processing...' : 'Register Only'}
              </button>
              <button
                type="submit"
                onClick={() => {
                  // Free registration + redirect to donation
                  setFormData(prev => ({ ...prev, _redirectTarget: 'donate' }));
                }}
                style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', opacity: isLoading ? 0.7 : 1 }}
                disabled={isLoading}
              >
                {isLoading ? 'Processing...' : 'Register & Donate 💖'}
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
}
